"use client";

import { useState, useEffect, useRef } from "react";
import { Camera, Loader2, AlertTriangle } from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import { useAuth } from "@/lib/auth";
import { AvatarStore, ownerAvatarPath } from "@/lib/storage/avatars.store";
import { timestampedFileName } from "@/lib/storage/paths";
import { strings } from "@/lib/strings";
import type { DBUser } from "@/lib/db";
import { type NotificationSettings, DEFAULT_NOTIFICATION_SETTINGS } from "@/lib/email/types";
import { Modal, ModalBody, ModalFooter } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { FormField } from "@/components/ui/FormField";
import { Checkbox } from "@/components/ui/Checkbox";
import { InfoCallout } from "@/components/ui/InfoCallout";
import { ProfilePhoneField } from "@/components/profile/ProfilePhoneField";
import type { LoginPhoneState } from "@/lib/auth/login-phone.service";
import { isPlaceholderEmail } from "@/lib/users/types";
interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * The avatars bucket's `allowed_mime_types`, and the extensions they map to.
 * Kept together so the accepted TYPE and the stored EXTENSION cannot drift —
 * that drift is what let an SVG be stored as `.jpg`.
 */
const ALLOWED_AVATAR_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const ALLOWED_AVATAR_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "gif"];
type CompanyType = "landowner" | "buyer" | "both" | "";
type FormData = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
  company_type: CompanyType;
  tax_id: string;
  address_line1: string;
  address_line2: string;
  postal_code: string;
  city: string;
  country: string;
  preferred_locale: "de-DE";
};
const initialFormData: FormData = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company_name: "",
  company_type: "",
  tax_id: "",
  address_line1: "",
  address_line2: "",
  postal_code: "",
  city: "",
  country: "DE",
  preferred_locale: "de-DE"
};

/**
 * The one write seam. Every profile mutation in this modal goes through
 * `PATCH /api/user/profile`, which owns the field allow-list, validates the
 * structured fields, and ensures the row exists — none of which a client-side
 * upsert under RLS could do.
 */
async function patchProfile(updates: Record<string, unknown>): Promise<void> {
  const response = await fetch("/api/user/profile", {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(updates)
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error ?? "Failed to update profile");
  }
}
export function ProfileModal({
  isOpen,
  onClose
}: ProfileModalProps) {
  const {
    user,
    isAuthenticated,
    signOut
  } = useAuth();
  const t = strings;
  const supabase = createClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>(DEFAULT_NOTIFICATION_SETTINGS);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  /** The stored jsonb, verbatim — see the merge in handleSubmit. */
  const [storedNotificationSettings, setStoredNotificationSettings] = useState<Record<string, unknown> | null>(null);
  /**
   * The profile READ failed (as opposed to "there is no row yet"). Saving from
   * this state would write the empty form over real data, so the form is not
   * savable until a reload succeeds.
   */
  const [loadFailed, setLoadFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  /** The LOGIN phone from GoTrue (ARCH-055) — distinct from `formData.phone`, the callback number. */
  const [loginPhone, setLoginPhone] = useState<LoginPhoneState | null>(null);

  // Keyed on the user's VALUES, not the object: the auth context hands out a
  // new user object on every auth event (tab focus, token refresh), and a
  // reload on each one remounted the form — wiping a login-phone change in
  // progress, or any half-typed edit.
  const userId = user?.id;
  const userEmail = user?.email;
  const userPhone = user?.phone;

  // Fetch user profile on mount
  useEffect(() => {
    const loadProfile = async () => {
      if (!userId) return;
      setIsLoading(true);
      setError(null);
      setLoadFailed(false);
      try {
        // `GET /api/user/profile` — the sanctioned read.
        //
        // ONLY 404 means "no profile row yet". Treating every non-2xx that way
        // renders a normal-looking BLANK form after a 500 or an expired
        // session, and the next Speichern writes null over first_name,
        // last_name, company, tax_id and the whole address — with a success
        // banner. It would also reset notification_settings to the all-true
        // default, silently re-opting-in someone who had opted out.
        const response = await fetch("/api/user/profile");
        if (!response.ok && response.status !== 404) {
          throw new Error(`Profil konnte nicht geladen werden (${response.status})`);
        }
        const body = (await response.json().catch(() => ({}))) as {
          profile?: DBUser;
          login_phone?: LoginPhoneState;
        };
        const data: DBUser | null = response.ok ? body.profile ?? null : null;
        setLoginPhone(body.login_phone ?? null);
        if (data) {
          const profile = data;
          setFormData({
            first_name: profile.first_name || "",
            last_name: profile.last_name || "",
            email: profile.email || userEmail || "",
            phone: profile.phone || userPhone || "",
            company_name: profile.company_name || "",
            company_type: profile.company_type as CompanyType || "",
            tax_id: (profile as Record<string, unknown>).tax_id as string || "",
            address_line1: profile.address_line1 || "",
            address_line2: profile.address_line2 || "",
            postal_code: profile.postal_code || "",
            city: profile.city || "",
            country: profile.country || "DE",
            preferred_locale: "de-DE"
          });
          setAvatarUrl(profile.avatar_url);
          // Kept verbatim so a save MERGES onto it. `UserRepository.update`
          // replaces the whole jsonb, and the column really holds five keys
          // (1980 of 2036 production rows) while this modal only toggles three
          // — writing the 3-key object destroyed `digest_day` and
          // `email_weekly_digest`, which the weekly-digest query reads.
          setStoredNotificationSettings(profile.notification_settings as Record<string, unknown> | null ?? null);
          if (profile.notification_settings && typeof profile.notification_settings === "object" && !Array.isArray(profile.notification_settings)) {
            const settings = profile.notification_settings as Record<string, unknown>;
            setNotificationSettings({
              ...DEFAULT_NOTIFICATION_SETTINGS,
              email_match_alerts: typeof settings.email_match_alerts === "boolean" ? settings.email_match_alerts : DEFAULT_NOTIFICATION_SETTINGS.email_match_alerts,
              email_message_alerts: typeof settings.email_message_alerts === "boolean" ? settings.email_message_alerts : DEFAULT_NOTIFICATION_SETTINGS.email_message_alerts,
              email_deal_updates: typeof settings.email_deal_updates === "boolean" ? settings.email_deal_updates : DEFAULT_NOTIFICATION_SETTINGS.email_deal_updates
            });
          }
        } else {
          // No row yet. Nothing to write here — `PATCH /api/user/profile`
          // ensures the row on the first save, server-side, so the modal no
          // longer needs its own upsert (which ran under RLS and could set
          // email/phone unverified).
          setFormData(prev_0 => ({
            ...prev_0,
            email: userEmail || "",
            phone: userPhone || ""
          }));
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        // SURFACE IT, and mark the load as failed. Swallowing this rendered a
        // normal-looking blank form, and one click of Speichern then wrote null
        // over the whole stored profile — including resetting the notification
        // preferences to the all-true default.
        setLoadFailed(true);
        setError(err instanceof Error ? err.message : "Profil konnte nicht geladen werden");
        setFormData(prev => ({
          ...prev,
          email: userEmail || "",
          phone: userPhone || ""
        }));
      } finally {
        setIsLoading(false);
      }
    };
    if (isOpen && isAuthenticated && userId) {
      loadProfile();
    }
  }, [isOpen, isAuthenticated, userId, userEmail, userPhone]);

  // Reset success state after delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  /**
   * After a login-phone change, re-read the contact fields: the server mirrors
   * the new number into the callback number only when that WAS the old login
   * (or empty), and only it knows which normalized number that was.
   */
  const handleLoginPhoneChanged = async (next: LoginPhoneState) => {
    setLoginPhone(next);
    try {
      const response_0 = await fetch("/api/user/profile");
      if (!response_0.ok) return;
      const body_0 = (await response_0.json()) as {
        profile?: DBUser;
        login_phone?: LoginPhoneState;
      };
      if (body_0.login_phone) setLoginPhone(body_0.login_phone);
      if (body_0.profile) setFormData(prev_1 => ({
        ...prev_1,
        phone: body_0.profile?.phone || ""
      }));
    } catch {
      // The change is applied; the callback line just stays as it was until reload.
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev_2 => ({
      ...prev_2,
      [name]: value
    }));
  };
  const handleNotificationToggle = (key: keyof NotificationSettings, checked: boolean) => {
    setNotificationSettings(prev_3 => ({
      ...prev_3,
      [key]: checked
    }));
  };
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };
  const handleAvatarChange = async (e_0: React.ChangeEvent<HTMLInputElement>) => {
    const file = e_0.target.files?.[0];
    if (!file || !user) return;

    // Narrowed from `startsWith("image/")` to exactly the bucket's
    // `allowed_mime_types`. The looser check accepted image/svg+xml, /avif,
    // /heic and /tiff, which `timestampedFileName` then stored under a forced
    // `.jpg` — an object key that lies about its content, recorded permanently
    // in `users.avatar_url`. The bucket would have rejected them anyway; this
    // says so before the upload instead of after.
    if (!ALLOWED_AVATAR_MIME_TYPES.includes(file.type)) {
      setError("Please select a JPG, PNG, WebP or GIF image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be less than 5MB");
      return;
    }
    setIsUploadingAvatar(true);
    setError(null);
    try {
      // `{user_id}/{timestamp}.{ext}`. NOTE: unlike deal-documents, the avatars
      // bucket's policies do NOT check the folder against auth.uid() — see the
      // caveat in `@/lib/storage/avatars.store`. The owner prefix is a shape the
      // missing policy could later be added against without a data migration,
      // not a guarantee today.
      const filePath = ownerAvatarPath(user.id, timestampedFileName(file.name, ALLOWED_AVATAR_EXTENSIONS, "jpg"));
      const publicUrl = await AvatarStore.upload(supabase, filePath, file, {
        contentType: file.type,
        upsert: true
      });
      await patchProfile({
        avatar_url: publicUrl
      });
      setAvatarUrl(publicUrl);
    } catch (err_0) {
      console.error("Error uploading avatar:", err_0);
      setError(t.profile.profileUpdateError);
    } finally {
      setIsUploadingAvatar(false);
    }
  };
  const handleRemoveAvatar = async () => {
    if (!user || !avatarUrl) return;
    setIsUploadingAvatar(true);
    setError(null);
    try {
      await patchProfile({
        avatar_url: null
      });
      setAvatarUrl(null);
    } catch (err_1) {
      console.error("Error removing avatar:", err_1);
      setError(t.profile.profileUpdateError);
    } finally {
      setIsUploadingAvatar(false);
    }
  };
  const handleSubmit = async (e_1: React.FormEvent) => {
    e_1.preventDefault();
    if (!user) return;
    setIsSaving(true);
    setError(null);
    setSuccess(false);
    try {
      // NO `id`, and NO `email`/`phone`: the route derives the user from the
      // session, and identity changes go through the OTP services rather than a
      // profile save (the inputs are read-only for the same reason).
      const profileData = {
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        company_name: formData.company_name || null,
        company_type: formData.company_type || null,
        tax_id: formData.tax_id || null,
        address_line1: formData.address_line1 || null,
        address_line2: formData.address_line2 || null,
        postal_code: formData.postal_code || null,
        city: formData.city || null,
        country: formData.country,
        preferred_locale: formData.preferred_locale,
        // NO `avatar_url`. The two avatar handlers each PATCH it themselves, so
        // echoing the loaded value here only creates a way for a value the user
        // never touched to reject the whole save — which it did: production
        // holds avatars on a different origin from the current
        // NEXT_PUBLIC_SUPABASE_URL, and those users could not save their name.
        notification_settings: {
          ...(storedNotificationSettings ?? {}),
          ...notificationSettings
        }
      };
      await patchProfile(profileData);
      setSuccess(true);
    } catch (err_2) {
      console.error("Error updating profile:", err_2);
      // Surface the server's reason. `patchProfile` already extracts
      // `body.error`; discarding it made a validation refusal, a 401 and a
      // dropped connection one indistinguishable sentence, with no way for the
      // user or support to tell which.
      setError(err_2 instanceof Error ? err_2.message : t.profile.profileUpdateError);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (deleteConfirmText !== "LÖSCHEN") return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const response_1 = await fetch("/api/user/delete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          source: "profile"
        })
      });
      if (!response_1.ok) {
        const data_0 = await response_1.json();
        throw new Error(data_0.error || "Fehler beim Löschen des Kontos");
      }
      await signOut();
      onClose();
      window.location.href = "/";
    } catch (err_3) {
      setDeleteError(err_3 instanceof Error ? err_3.message : "Ein Fehler ist aufgetreten");
      setIsDeleting(false);
    }
  };
  return <Modal open={isOpen} onClose={onClose} title={t.profile.title} description={t.profile.subtitle} size="md">
      {isLoading ? <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-foreground-muted" />
        </div> : <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <ModalBody>
            <div className="space-y-6">
              {/* Error/Success Messages */}
              {error && <InfoCallout variant="error">{error}</InfoCallout>}
              {success && <InfoCallout variant="success">{t.profile.profileUpdated}</InfoCallout>}

              {/* Avatar Section */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  <button type="button" onClick={handleAvatarClick} disabled={isUploadingAvatar} className="relative w-20 h-20 rounded-full overflow-hidden bg-surface-container-low hover:bg-surface-container transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
                    {avatarUrl ?
                // eslint-disable-next-line @next/next/no-img-element
                <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center">
                        <Camera className="w-8 h-8 text-foreground-muted" />
                      </div>}
                    {isUploadingAvatar && <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 text-white animate-spin" />
                      </div>}
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
                </div>
                <div className="flex-1">
                  <p className="text-body font-medium text-foreground">{t.profile.photo}</p>
                  <div className="flex gap-2 mt-1">
                    <button type="button" onClick={handleAvatarClick} disabled={isUploadingAvatar} className="text-body-sm text-foreground-muted hover:text-foreground underline">
                      {avatarUrl ? t.profile.changePhoto : t.profile.uploadPhoto}
                    </button>
                    {avatarUrl && <button type="button" onClick={handleRemoveAvatar} disabled={isUploadingAvatar} className="text-body-sm text-red-500 hover:text-red-700 underline">
                        {t.profile.removePhoto}
                      </button>}
                  </div>
                </div>
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <FormField label={t.profile.firstName} htmlFor="first_name">
                  <Input id="first_name" name="first_name" inputSize="compact" value={formData.first_name} onChange={handleInputChange} />
                </FormField>
                <FormField label={t.profile.lastName} htmlFor="last_name">
                  <Input id="last_name" name="last_name" inputSize="compact" value={formData.last_name} onChange={handleInputChange} />
                </FormField>
              </div>

              {/* Contact Fields */}
              <div className="space-y-4">
                <FormField label={t.profile.email} htmlFor="email">
                  {/* READ-ONLY: `users.email` is authentication identity and
                      notification routing. Changing it goes through the OTP
                      services (`syncVerifiedEmail`), never a profile save —
                      this input used to write it unverified via a client-side
                      upsert under RLS. A phone-only account's placeholder
                      address is no address to show. */}
                  <Input id="email" name="email" type="email" inputSize="compact" value={isPlaceholderEmail(formData.email) ? "" : formData.email} placeholder={t.profile.noEmail} readOnly />
                </FormField>
                {/* The LOGIN phone: added or changed only behind a step-up
                    (ARCH-055), never through the profile save — the PATCH
                    allow-list has no `phone`. */}
                {loginPhone && <ProfilePhoneField state={loginPhone} callbackPhone={formData.phone || null} onChanged={handleLoginPhoneChanged} />}
              </div>

              {/* Company Fields */}
              <div className="space-y-4">
                <h3 className="text-base font-bold text-primary">{t.profile.company}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label={t.profile.companyName} htmlFor="company_name">
                    <Input id="company_name" name="company_name" inputSize="compact" value={formData.company_name} onChange={handleInputChange} />
                  </FormField>
                  <FormField label={t.profile.companyType} htmlFor="company_type">
                    <Select id="company_type" name="company_type" selectSize="compact" value={formData.company_type} onChange={handleInputChange} options={[{
                  value: "",
                  label: "-"
                }, {
                  value: "landowner",
                  label: t.profile.companyTypeLandowner
                }, {
                  value: "buyer",
                  label: t.profile.companyTypeBuyer
                }, {
                  value: "both",
                  label: t.profile.companyTypeBoth
                }]} />
                  </FormField>
                </div>
                <FormField label="Steuer-ID" htmlFor="tax_id" helpText={t.common.optional}>
                  <Input id="tax_id" name="tax_id" inputSize="compact" value={formData.tax_id} onChange={handleInputChange} placeholder="12345678901" />
                </FormField>
              </div>

              {/* Address Fields */}
              <div className="space-y-4">
                <h3 className="text-base font-bold text-primary">{t.profile.address}</h3>
                <FormField label={t.profile.addressLine1} htmlFor="address_line1">
                  <Input id="address_line1" name="address_line1" inputSize="compact" value={formData.address_line1} onChange={handleInputChange} />
                </FormField>
                <FormField label={t.profile.addressLine2} htmlFor="address_line2" helpText={t.common.optional}>
                  <Input id="address_line2" name="address_line2" inputSize="compact" value={formData.address_line2} onChange={handleInputChange} />
                </FormField>
                <div className="grid grid-cols-3 gap-4">
                  <FormField label={t.profile.postalCode} htmlFor="postal_code">
                    <Input id="postal_code" name="postal_code" inputSize="compact" value={formData.postal_code} onChange={handleInputChange} />
                  </FormField>
                  <div className="col-span-2">
                    <FormField label={t.profile.city} htmlFor="city">
                      <Input id="city" name="city" inputSize="compact" value={formData.city} onChange={handleInputChange} />
                    </FormField>
                  </div>
                </div>
              </div>

              {/* Notification Settings */}
              <div className="space-y-4" id="benachrichtigungen">
                <div>
                  <h3 className="text-base font-bold text-primary">{t.profile.notifications}</h3>
                  <p className="text-body-sm text-foreground-muted mt-0.5">
                    {t.profile.notificationsSubtitle}
                  </p>
                </div>

                <div className="space-y-3">
                  <Checkbox id="email_match_alerts" checked={notificationSettings.email_match_alerts} onChange={e_2 => handleNotificationToggle("email_match_alerts", e_2.target.checked)} label={t.profile.emailMatchAlerts} description={t.profile.emailMatchAlertsDesc} />
                  <Checkbox id="email_message_alerts" checked={notificationSettings.email_message_alerts} onChange={e_3 => handleNotificationToggle("email_message_alerts", e_3.target.checked)} label={t.profile.emailMessageAlerts} description={t.profile.emailMessageAlertsDesc} />
                </div>
              </div>

              {/* Danger Zone */}
              <div className="space-y-4 border-t border-red-200 pt-6 mt-6" id="gefahrenbereich">
                <div>
                  <h3 className="text-base font-bold text-red-600">{t.profile.deleteAccount}</h3>
                  <p className="text-body-sm text-foreground-muted mt-0.5">
                    {t.profile.deleteAccountWarning}
                  </p>
                </div>

                {!showDeleteConfirm ? <Button type="button" variant="outline" size="sm" onClick={() => setShowDeleteConfirm(true)} className="text-red-600 border-red-300 hover:bg-red-50">
                    {t.profile.deleteAccount}
                  </Button> : <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                      <p className="text-sm text-red-800">
                        Alle Ihre Angebote, Anfragen und Nachrichten werden unwiderruflich gelöscht.
                        Geben Sie <strong>LÖSCHEN</strong> ein, um zu bestätigen.
                      </p>
                    </div>
                    <Input inputSize="compact" value={deleteConfirmText} onChange={e_4 => setDeleteConfirmText(e_4.target.value)} placeholder='Geben Sie "LÖSCHEN" ein' className="border-red-300 focus:ring-red-500/20 focus:border-red-500" />
                    {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
                    <div className="flex gap-2">
                      <Button type="button" variant="danger" size="sm" loading={isDeleting} disabled={deleteConfirmText !== "LÖSCHEN"} onClick={handleDelete}>
                        {t.profile.confirmDelete}
                      </Button>
                      <Button type="button" variant="ghost" size="sm" disabled={isDeleting} onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText("");
                  setDeleteError(null);
                }}>
                        {t.common.cancel}
                      </Button>
                    </div>
                  </div>}
              </div>
            </div>
          </ModalBody>

          <ModalFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t.common.cancel}
            </Button>
            {/* Also blocked while an avatar is uploading: handleSubmit reads
                `avatarUrl` from state, so a save started mid-upload would race
                the avatar's own PATCH and could land second with the stale
                value, clearing the row's avatar while the UI already shows the
                new one. */}
            <Button type="submit" loading={isSaving} disabled={isUploadingAvatar || loadFailed}>
              {t.profile.saveChanges}
            </Button>
          </ModalFooter>
        </form>}
    </Modal>;
}